<script setup>
import { computed } from 'vue';
import Spinner from 'shared/components/Spinner.vue';

const props = defineProps({
  data: {
    type: Object,
    default: () => ({}),
  },
  loading: {
    type: Boolean,
    default: false,
  },
  title: {
    type: String,
    default: 'Distribuicao de Carga',
  },
});

const balance = computed(() => props.data?.balance_status || 'unknown');
const standardDeviation = computed(() => props.data?.standard_deviation || 0);
const avgWorkload = computed(() => props.data?.avg_workload || 0);
const alerts = computed(() => props.data?.alerts || []);
const agentWorkloads = computed(() => props.data?.agent_workloads || []);

const maxWorkload = computed(() => {
  if (agentWorkloads.value.length === 0) return 1;
  return Math.max(...agentWorkloads.value.map(a => a.workload), 1);
});

function getBalanceColor(status) {
  switch (status) {
    case 'balanced':
      return {
        bg: 'bg-n-teal-3',
        text: 'text-n-teal-11',
        icon: 'i-lucide-check-circle',
      };
    case 'slightly_unbalanced':
      return {
        bg: 'bg-n-amber-3',
        text: 'text-n-amber-11',
        icon: 'i-lucide-alert-circle',
      };
    case 'unbalanced':
      return {
        bg: 'bg-n-ruby-3',
        text: 'text-n-ruby-11',
        icon: 'i-lucide-alert-triangle',
      };
    default:
      return {
        bg: 'bg-n-slate-3',
        text: 'text-n-slate-11',
        icon: 'i-lucide-help-circle',
      };
  }
}

function getBalanceLabel(status) {
  switch (status) {
    case 'balanced':
      return 'Equilibrada';
    case 'slightly_unbalanced':
      return 'Ligeiramente Desbalanceada';
    case 'unbalanced':
      return 'Desbalanceada';
    default:
      return 'Desconhecido';
  }
}

function getWorkloadColor(workload) {
  const ratio = workload / avgWorkload.value;
  if (ratio <= 0.5) return 'bg-n-blue-9';
  if (ratio <= 1.2) return 'bg-n-teal-9';
  if (ratio <= 1.5) return 'bg-n-amber-9';
  return 'bg-n-ruby-9';
}

function formatNumber(num) {
  if (!num) return '0';
  return num.toLocaleString('pt-BR');
}
</script>

<template>
  <div class="flex flex-col rounded-xl border border-n-weak bg-n-solid-2 p-4">
    <div class="flex items-center justify-between mb-4">
      <div class="flex items-center gap-2">
        <span class="i-lucide-scale text-lg text-n-violet-10" />
        <h3 class="text-base font-semibold text-n-slate-12">{{ title }}</h3>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="flex items-center justify-center h-64">
      <Spinner size="large" />
    </div>

    <div v-else class="flex flex-col gap-4">
      <!-- Balance Status Card -->
      <div
        class="flex items-center gap-4 p-4 rounded-lg"
        :class="getBalanceColor(balance).bg"
      >
        <span
          class="text-2xl"
          :class="[
            getBalanceColor(balance).icon,
            getBalanceColor(balance).text,
          ]"
        />
        <div class="flex-1">
          <div
            class="text-sm font-medium"
            :class="getBalanceColor(balance).text"
          >
            {{ getBalanceLabel(balance) }}
          </div>
          <div class="text-xs text-n-slate-11 mt-0.5">
            Desvio padrao: {{ standardDeviation.toFixed(1) }} | Media:
            {{ formatNumber(avgWorkload) }} conv/agente
          </div>
        </div>
      </div>

      <!-- Alerts -->
      <div v-if="alerts.length > 0" class="flex flex-col gap-2">
        <div
          v-for="(alert, index) in alerts"
          :key="index"
          class="flex items-center gap-2 p-2 rounded-lg bg-n-amber-3/50 text-xs"
        >
          <span class="i-lucide-alert-circle text-n-amber-10" />
          <span class="text-n-amber-11">{{ alert }}</span>
        </div>
      </div>

      <!-- Agent Workload Bars -->
      <div v-if="agentWorkloads.length > 0" class="flex flex-col gap-2">
        <div class="text-xs font-medium text-n-slate-11 uppercase mb-1">
          Carga por Agente
        </div>
        <div class="flex flex-col gap-2 max-h-52 overflow-y-auto">
          <div
            v-for="agent in agentWorkloads"
            :key="agent.agent_id"
            class="flex items-center gap-3"
          >
            <span
              class="text-xs text-n-slate-11 w-24 truncate"
              :title="agent.name"
            >
              {{ agent.name }}
            </span>
            <div class="flex-1 h-4 bg-n-alpha-2 rounded overflow-hidden">
              <div
                class="h-full rounded transition-all duration-300"
                :class="getWorkloadColor(agent.workload)"
                :style="{ width: `${(agent.workload / maxWorkload) * 100}%` }"
              />
            </div>
            <span class="text-xs font-medium text-n-slate-12 w-12 text-right">
              {{ agent.workload }}
            </span>
          </div>
        </div>
      </div>

      <!-- Legend -->
      <div
        class="flex items-center justify-center gap-4 pt-3 border-t border-n-weak text-xs"
      >
        <div class="flex items-center gap-1">
          <span class="w-2 h-2 rounded bg-n-blue-9" />
          <span class="text-n-slate-11">Baixa</span>
        </div>
        <div class="flex items-center gap-1">
          <span class="w-2 h-2 rounded bg-n-teal-9" />
          <span class="text-n-slate-11">Ideal</span>
        </div>
        <div class="flex items-center gap-1">
          <span class="w-2 h-2 rounded bg-n-amber-9" />
          <span class="text-n-slate-11">Alta</span>
        </div>
        <div class="flex items-center gap-1">
          <span class="w-2 h-2 rounded bg-n-ruby-9" />
          <span class="text-n-slate-11">Sobrecarregado</span>
        </div>
      </div>
    </div>
  </div>
</template>
